<script setup lang="ts">
const steps = [
  {
    number: '01',
    title: 'Discovery Call',
    description: 'We discuss your project requirements, technical challenges, and business goals in a free 30-minute consultation.',
  },
  {
    number: '02',
    title: 'Proposal & Scope',
    description: 'You receive a detailed proposal with fixed pricing, timeline, and technical specification within 5 business days.',
  },
  {
    number: '03',
    title: 'Development',
    description: 'Agile sprints with weekly demos. Real-time progress with staging environments and continuous deployment.',
  },
  {
    number: '04',
    title: 'Launch & Support',
    description: 'We handle deployment, DNS, SSL, and monitoring. Optional SLA support packages for ongoing maintenance.',
  },
]

</script>

<template>
  <section id="process" class="py-20 md:py-40 relative">
    <div class="max-w-7xl mx-auto px-6 md:px-12">
      <div class="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <!-- Left: Content -->
        <div data-aos="fade">
          <div class="flex items-center gap-3 mb-6">
            <span class="text-xs tracking-[0.25em] text-blu-900 dark:text-blu-400 uppercase font-medium">Process</span>
          </div>

          <h2 class="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-700 dark:text-white mb-6">
            How we work
          </h2>

          <p class="text-lg md:text-xl text-slate-700/80 dark:text-white/65 leading-relaxed text-pretty max-w-xl mb-10">
            Every project runs through the same four steps, from the first call to launch day, so the timeline stays honest and you always know what's next.
          </p>

          <NuxtLink to="/contact" class="font-medium text-blu-600 underline decoration-dotted underline-offset-4 transition-colors hover:decoration-solid dark:text-blu-400">Book a discovery call</NuxtLink>
        </div>

        <!-- Right: the steps in order, as rows divided by hairlines, on the
             same dot stage as the other sections. -->
        <VisualStage>
          <ol class="relative divide-y divide-slate-900/10 dark:divide-white/10">
            <li v-for="(step, index) in steps" :key="step.number" data-aos="fade" :data-aos-delay="150 + index * 120" class="flex gap-6 px-3 py-6">
              <span class="step-number w-10 shrink-0 text-2xl font-semibold text-blu-600 dark:text-blu-400">{{ step.number }}</span>
              <div>
                <h3 class="text-lg font-semibold text-slate-700 dark:text-white">{{ step.title }}</h3>
                <p class="mt-1 text-[15px] leading-relaxed text-slate-700/75 dark:text-white/55">{{ step.description }}</p>
              </div>
            </li>
          </ol>
        </VisualStage>
      </div>
    </div>
  </section>
</template>

<style scoped>
.step-number {
  font-variant-numeric: tabular-nums;
}
</style>
